import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import {
  addMultipleQuestionsToTest,
  deleteTestQuestion,
  getTests,
  movePassageInTest,
  patchTest,
  patchTestQuestion,
  removePassageFromTest,
  reorderTestQuestion,
  uploadTestAdditionalInformation,
} from "../../../services/Exams/ExamsService";
import { getApiErrorCode } from "../../../utils/apiError";
import { isCanceledError } from "../../../utils/examError";
import { compositionUnits, placePassage } from "../../../utils/passages";

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);
// Answers that refuse a write as a whole without anything being lost (the test is loaded again after them).
const REFUSALS = ["not_approved", "already_in_test"];
const pendingUuid = (questionUuid) => `pending-${questionUuid}`;
// A created TestQuestion carries the full question; keep the list row if an older API sends only its uuid.
const withQuestion = (tq, fallback) => ({ ...tq, question: tq.question && typeof tq.question === "object" ? tq.question : fallback });
// `rows` are {question, points?}: a row without points of its own gets its question's difficulty points
// (question.points: 3/5/10, null without difficulty), then the shared `points`.
// `test` is only needed by older backends; `order` is left out so the server appends in request order.
const addPayload = (testUuid, rows, points) => ({
  questions: rows.map((row) => ({
    test: testUuid,
    question_id: row.question.uuid,
    points: row.points ?? row.question.points ?? points,
  })),
});

// The rows of a passage (question.passage) form a block in the test.
const passageOf = (item) => item?.question?.passage?.uuid || null;
const firstRowOf = (list, passageUuid) => list.findIndex((it) => passageOf(it) === passageUuid);
const isApart = (list, passageUuid) => compositionUnits(list).some((u) => u.kind === "passage" && u.passage.uuid === passageUuid && u.split);
// The questions of a passage go into one request together, where its first one was picked.
const groupByPassage = (questions) => {
  const groups = [];
  const byPassage = new Map();
  questions.forEach((q) => {
    const uuid = q.passage?.uuid;
    if (!uuid) {
      groups.push([q]);
    } else if (byPassage.has(uuid)) {
      byPassage.get(uuid).push(q);
    } else {
      byPassage.set(uuid, [q]);
      groups.push(byPassage.get(uuid));
    }
  });
  return groups.flat();
};
// New rows are shown where they end up: after the rows of their passage (the server appends, and the
// block is gathered right after), otherwise at the end.
const placeNewRows = (list, rows) => {
  const next = [...list];
  rows.forEach((row) => {
    const uuid = passageOf(row);
    const last = uuid ? next.map(passageOf).lastIndexOf(uuid) : -1;
    if (last < 0) next.push(row);
    else next.splice(last + 1, 0, row);
  });
  return next;
};
// The rows in the order move_passage answered ([{uuid, order}] for every row of the test), or null when
// the answer does not match them.
const sortByOrders = (list, orders) => {
  if (!Array.isArray(orders)) return null;
  const orderOf = new Map(orders.map((row) => [row?.uuid, Number(row?.order)]));
  if (orderOf.size !== list.length || !list.every((it) => Number.isFinite(orderOf.get(it.uuid)))) return null;
  return [...list].sort((a, b) => orderOf.get(a.uuid) - orderOf.get(b.uuid));
};

/**
 * A test and its questions (TestQuestion {uuid, order, points, question}), changed optimistically.
 * Writes run one at a time through a queue: the backend renumbers orders on every add, remove and
 * reorder, so overlapping writes would race. When a write fails (or the server skipped something),
 * the server state is reloaded once the queue is empty, so later optimistic changes are not lost.
 * Rows added locally and not saved yet have `pending: true` and a temporary uuid.
 * The rows of a passage are a block: move_passage moves (or gathers) it, remove_passage removes it.
 * An add refused with 400 not_approved (+ `questions`: not approved, and their exam lets only approved ones into
 * tests) or 400 already_in_test (in the test already: the builder's adds skip those, a single-row endpoint says so)
 * writes nothing: its rows go again and the error goes to the caller, without the "not saved" state; the server
 * state is loaded all the same (the rows' rule and statuses, or the test's rows, are out of date).
 */
export default function useTestBuilder(testUuid) {
  const [test, setTest] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [inFlight, setInFlight] = useState(0);
  const [lastWrite, setLastWrite] = useState(null); // null | "ok" | "error"
  const queueRef = useRef(Promise.resolve());
  const itemsRef = useRef(items);
  const opsRef = useRef(0); // writes queued or running
  const genRef = useRef(0); // bumped by every write: a reload started before it is outdated
  const staleRef = useRef(false); // local state may differ from the server: reload when the queue drains
  itemsRef.current = items;

  // Resolves true when the server state was applied.
  const reload = useCallback(
    async ({ signal, silent = false } = {}) => {
      const gen = genRef.current;
      if (!silent) setLoading(true);
      try {
        const data = await getTests({ params: { uuid: testUuid }, signal });
        // Answered after it was replaced (a new uuid) or the builder left: the answer is outdated.
        if (signal?.aborted) return false;
        if (genRef.current !== gen) {
          // A write started meanwhile: this snapshot would drop its optimistic rows.
          staleRef.current = true;
          return false;
        }
        setTest(data);
        setItems([...(data?.test_questions || [])].sort(byOrder));
        setLoadError(null);
        return true;
      } catch (err) {
        if (!isCanceledError(err) && !silent) setLoadError(err);
        return false;
      } finally {
        // A load canceled for a newer one (StrictMode's double effect, a new uuid) leaves `loading` to that one.
        if (!silent && !signal?.aborted) setLoading(false);
      }
    },
    [testUuid]
  );

  useEffect(() => {
    const controller = new AbortController();
    reload({ signal: controller.signal });
    return () => controller.abort();
  }, [reload]);

  // Another tab or user may have changed the test meanwhile.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && opsRef.current === 0) reload({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [reload]);

  const enqueue = useCallback(
    (op) => {
      genRef.current += 1;
      opsRef.current += 1;
      setInFlight((n) => n + 1);
      const run = queueRef.current
        .then(op)
        .then(
          (value) => {
            setLastWrite("ok");
            return value;
          },
          (err) => {
            // An add the server refused as a whole (400 not_approved: questions their exam does not take into tests
            // yet; 400 already_in_test: a question is in the test already, e.g. added in another tab) wrote nothing,
            // and its rows are already taken back: the caller says why, nothing is unsaved.
            // The test is still loaded again: the refusal shows that an exam's rule, a question's status or the test
            // changed since it was loaded, so rows already in it may have become ones that can't come back once
            // removed, and a question added elsewhere shows (once).
            if (!REFUSALS.includes(getApiErrorCode(err))) setLastWrite("error");
            staleRef.current = true;
            throw err;
          }
        )
        .finally(() => {
          opsRef.current -= 1;
          setInFlight((n) => n - 1);
          if (opsRef.current === 0 && staleRef.current) {
            staleRef.current = false;
            reload({ silent: true });
          }
        });
      queueRef.current = run.catch(() => {});
      return run;
    },
    [reload]
  );

  // move_passage answers with the order of every row. It is taken when no later write has changed the
  // rows meanwhile; otherwise (or when it does not fit) the server state is loaded once the queue is done.
  const applyOrders = useCallback((orders) => {
    const sorted = opsRef.current === 1 ? sortByOrders(itemsRef.current, orders) : null;
    if (!sorted) {
      staleRef.current = true;
      return;
    }
    // The server placed the rows differently from this list: its passage links may differ too, so load them.
    if (sorted.some((it, i) => it !== itemsRef.current[i])) staleRef.current = true;
    setItems((prev) => sortByOrders(prev, orders) || prev);
  }, []);

  // The server appends: a passage whose rows are apart after an add is gathered at its first row.
  // `model` is the server's rows after the add. A failed gather only reloads (the block shows as split).
  const gatherAdded = useCallback(
    async (model, added) => {
      let list = model;
      for (const passageUuid of new Set(added.map((q) => q.passage?.uuid).filter(Boolean))) {
        if (!isApart(list, passageUuid)) continue;
        list = placePassage(list, passageUuid, firstRowOf(list, passageUuid));
        setItems((prev) => placePassage(prev, passageUuid, firstRowOf(prev, passageUuid)));
        try {
          const orders = await movePassageInTest({ params: { uuid: testUuid }, data: { passage: passageUuid, order: firstRowOf(list, passageUuid) + 1 } });
          applyOrders(orders);
        } catch {
          staleRef.current = true;
          return;
        }
      }
    },
    [applyOrders, testUuid]
  );

  // Appends bank questions with their difficulty points, or `points` for a question without difficulty;
  // questions already in the test are skipped. New rows of a passage that is in the test already join
  // its block (move_passage in the same write).
  const addQuestions = useCallback(
    (questions, points) => {
      const present = new Set(itemsRef.current.map((it) => it.question?.uuid));
      const fresh = [];
      questions.forEach((q) => {
        if (!q?.uuid || present.has(q.uuid)) return;
        present.add(q.uuid);
        fresh.push(q);
      });
      if (!fresh.length) return Promise.resolve([]);
      const rows = groupByPassage(fresh).map((question) => ({
        uuid: pendingUuid(question.uuid),
        pending: true,
        points: question.points ?? points,
        question,
      }));
      const freshIds = new Set(fresh.map((q) => q.uuid));
      const dropPending = (prev) => prev.filter((it) => !(it.pending && freshIds.has(it.question?.uuid)));
      // The server's rows once the writes queued before this one are done and these are appended.
      const expected = [...itemsRef.current, ...rows];
      setItems((prev) => placeNewRows(prev, rows));
      return enqueue(async () => {
        try {
          const created = await addMultipleQuestionsToTest({ params: { uuid: testUuid }, data: addPayload(testUuid, rows, points) });
          const list = Array.isArray(created) ? created : [];
          const byQuestion = new Map(list.map((tq) => [tq.question?.uuid ?? tq.question, tq]));
          const settle = (prev) =>
            dropPending(
              prev.map((it) => (it.pending && byQuestion.has(it.question?.uuid) ? withQuestion(byQuestion.get(it.question.uuid), it.question) : it))
            );
          setItems(settle);
          // Skipped by the server (already added elsewhere): take its state once the queue is done.
          if (list.length < fresh.length) staleRef.current = true;
          // The passages to gather come from the server's rows: a bank row's passage can be outdated (linked
          // or unlinked since the list was loaded). A row placed by an outdated link is fixed by the reload.
          const model = settle(expected);
          const added = model.filter((it) => freshIds.has(it.question?.uuid));
          const clientPassage = new Map(fresh.map((q) => [q.uuid, q.passage?.uuid || null]));
          if (added.some((it) => passageOf(it) !== clientPassage.get(it.question?.uuid))) staleRef.current = true;
          await gatherAdded(model, added.map((it) => it.question));
          return list;
        } catch (err) {
          setItems(dropPending);
          throw err;
        }
      });
    },
    [enqueue, gatherAdded, testUuid]
  );

  // Removes a saved row; resolves with what restoreItem needs to put it back.
  const removeItem = useCallback(
    (item) => {
      if (!item || item.pending) return Promise.resolve(null);
      const index = itemsRef.current.findIndex((it) => it.uuid === item.uuid);
      setItems((prev) => prev.filter((it) => it.uuid !== item.uuid));
      return enqueue(async () => {
        await deleteTestQuestion({ params: { uuid: item.uuid } });
        return { question: item.question, points: item.points, index };
      });
    },
    [enqueue]
  );

  // Undo of a remove: add again (appended by the server), then move back to the old position. A row of a
  // passage whose block is still in the test goes back inside that block, wherever it has moved meanwhile.
  const restoreItem = useCallback(
    ({ question, points, index }) => {
      const list = itemsRef.current;
      if (list.some((it) => it.question?.uuid === question.uuid)) return Promise.resolve();
      let at = Math.min(index, list.length);
      const passageUuid = question.passage?.uuid;
      if (passageUuid && firstRowOf(list, passageUuid) >= 0) {
        const last = list.map(passageOf).lastIndexOf(passageUuid);
        at = Math.min(Math.max(at, firstRowOf(list, passageUuid)), last + 1);
      }
      const placeholder = { uuid: pendingUuid(question.uuid), pending: true, points, question };
      const dropPlaceholder = (prev) => prev.filter((it) => it.uuid !== placeholder.uuid);
      setItems((prev) => {
        const next = [...prev];
        next.splice(Math.min(at, next.length), 0, placeholder);
        return next;
      });
      return enqueue(async () => {
        try {
          const created = await addMultipleQuestionsToTest({ params: { uuid: testUuid }, data: addPayload(testUuid, [{ question, points }]) });
          const tq = Array.isArray(created) ? created[0] : null;
          if (!tq) {
            setItems(dropPlaceholder);
            staleRef.current = true;
            return;
          }
          setItems((prev) => prev.map((it) => (it.uuid === placeholder.uuid ? withQuestion(tq, question) : it)));
          await reorderTestQuestion({ params: { uuid: tq.uuid }, data: { order: at + 1 } });
        } catch (err) {
          setItems(dropPlaceholder);
          throw err;
        }
      });
    },
    [enqueue, testUuid]
  );

  const moveItem = useCallback(
    (itemUuid, toIndex) => {
      const list = itemsRef.current;
      const from = list.findIndex((it) => it.uuid === itemUuid);
      if (from < 0 || list[from].pending) return Promise.resolve();
      const target = Math.max(0, Math.min(toIndex, list.length - 1));
      if (from === target) return Promise.resolve();
      setItems((prev) => {
        const current = prev.findIndex((it) => it.uuid === itemUuid);
        return current < 0 ? prev : arrayMove(prev, current, target);
      });
      return enqueue(() => reorderTestQuestion({ params: { uuid: itemUuid }, data: { order: target + 1 } }));
    },
    [enqueue]
  );

  const setPoints = useCallback(
    (itemUuid, points) => {
      const item = itemsRef.current.find((it) => it.uuid === itemUuid);
      if (!item || item.pending || item.points === points) return Promise.resolve();
      setItems((prev) => prev.map((it) => (it.uuid === itemUuid ? { ...it, points } : it)));
      return enqueue(() => patchTestQuestion({ params: { uuid: itemUuid }, data: { points } }));
    },
    [enqueue]
  );

  // A passage's rows as one block after `k` of the other rows (rows apart are gathered); not while some
  // of them are being added.
  const movePassage = useCallback(
    (passageUuid, k) => {
      const list = itemsRef.current;
      if (list.some((it) => it.pending && passageOf(it) === passageUuid)) return Promise.resolve();
      const next = placePassage(list, passageUuid, k);
      if (next.every((it, i) => it === list[i])) return Promise.resolve();
      setItems((prev) => placePassage(prev, passageUuid, k));
      // The block's place after the snap out of other blocks, so the server does not have to pick one.
      const order = firstRowOf(next, passageUuid) + 1;
      return enqueue(async () => applyOrders(await movePassageInTest({ params: { uuid: testUuid }, data: { passage: passageUuid, order } })));
    },
    [applyOrders, enqueue, testUuid]
  );

  // Gathers a passage's rows where its first row is.
  const gatherPassage = useCallback(
    (passageUuid) => movePassage(passageUuid, Math.max(0, firstRowOf(itemsRef.current, passageUuid))),
    [movePassage]
  );

  // Removes a passage's rows; resolves with what restorePassage needs to put them back. Not while some
  // of them are being added: the server would remove those too once they are saved.
  const removePassage = useCallback(
    (passageUuid) => {
      const list = itemsRef.current;
      const rows = list.filter((it) => passageOf(it) === passageUuid);
      if (!rows.length || rows.some((it) => it.pending)) return Promise.resolve(null);
      const index = list.indexOf(rows[0]);
      setItems((prev) => prev.filter((it) => passageOf(it) !== passageUuid));
      return enqueue(async () => {
        await removePassageFromTest({ params: { uuid: testUuid }, data: { passage: passageUuid } });
        // The server removed the rows linked to the passage now, which may not be the ones linked here: load them.
        staleRef.current = true;
        return { passage: rows[0].question.passage, rows: rows.map((it) => ({ question: it.question, points: it.points })), index };
      });
    },
    [enqueue, testUuid]
  );

  // Undo of removePassage: the rows are added again with their own points (appended by the server),
  // then the block is moved back to its old position.
  const restorePassage = useCallback(
    ({ passage, rows, index }) => {
      const present = new Set(itemsRef.current.map((it) => it.question?.uuid));
      const fresh = rows.filter((row) => row.question?.uuid && !present.has(row.question.uuid));
      const passageUuid = passage?.uuid || fresh[0]?.question.passage?.uuid;
      if (!fresh.length || !passageUuid) return Promise.resolve();
      const at = Math.min(index, itemsRef.current.length);
      const placeholders = fresh.map(({ question, points }) => ({ uuid: pendingUuid(question.uuid), pending: true, points, question }));
      const placeholderIds = new Set(placeholders.map((it) => it.uuid));
      const dropPlaceholders = (prev) => prev.filter((it) => !placeholderIds.has(it.uuid));
      setItems((prev) => {
        const next = [...prev];
        next.splice(Math.min(at, next.length), 0, ...placeholders);
        return next;
      });
      return enqueue(async () => {
        try {
          const created = await addMultipleQuestionsToTest({ params: { uuid: testUuid }, data: addPayload(testUuid, fresh) });
          const list = Array.isArray(created) ? created : [];
          const byQuestion = new Map(list.map((tq) => [tq.question?.uuid ?? tq.question, tq]));
          setItems((prev) =>
            dropPlaceholders(
              prev.map((it) => (placeholderIds.has(it.uuid) && byQuestion.has(it.question?.uuid) ? withQuestion(byQuestion.get(it.question.uuid), it.question) : it))
            )
          );
          if (list.length < fresh.length) staleRef.current = true;
          if (!list.length) return;
          applyOrders(await movePassageInTest({ params: { uuid: testUuid }, data: { passage: passageUuid, order: at + 1 } }));
        } catch (err) {
          setItems(dropPlaceholders);
          throw err;
        }
      });
    },
    [applyOrders, enqueue, testUuid]
  );

  // A passage saved elsewhere (its dialog): rows of its questions show its name, rows of questions no
  // longer linked to it leave its block (`questions` missing: only the name changes). The ref is set
  // too, so a write right after this sees the new links.
  const syncPassage = useCallback(({ uuid, name, questions }) => {
    if (!uuid) return;
    const linked = Array.isArray(questions) ? new Set(questions.map((q) => q?.uuid)) : null;
    const relink = (list) =>
      list.map((it) => {
        const question = it.question;
        if (!question?.uuid) return it;
        const was = passageOf(it) === uuid;
        if (linked ? linked.has(question.uuid) : was) {
          const passage = { ...(was ? question.passage : null), uuid, name: name ?? question.passage?.name ?? "" };
          return was && question.passage.name === passage.name ? it : { ...it, question: { ...question, passage } };
        }
        return was ? { ...it, question: { ...question, passage: null } } : it;
      });
    itemsRef.current = relink(itemsRef.current);
    setItems(relink);
  }, []);

  // "Refresh" after a failed write: the server state, and the error notice goes away once it is shown.
  const refresh = useCallback(async () => {
    if (opsRef.current > 0) return;
    if (await reload({ silent: true })) setLastWrite(null);
  }, [reload]);

  // Resolves when every queued write has finished (failed ones included).
  const flush = useCallback(() => queueRef.current, []);

  // Test fields (name, description, page heading, calculator, additional information); the questions are kept.
  const updateTest = useCallback(
    async (data) => {
      const saved = await patchTest({ params: { uuid: testUuid }, data });
      setTest((prev) => ({ ...prev, ...saved }));
      return saved;
    },
    [testUuid]
  );

  const uploadInfoFile = useCallback(
    async (file) => {
      const saved = await uploadTestAdditionalInformation({ params: { uuid: testUuid }, file });
      setTest((prev) => ({ ...prev, ...saved }));
      return saved;
    },
    [testUuid]
  );

  const inTest = useMemo(() => new Set(items.map((it) => it.question?.uuid)), [items]);
  const pendingIds = useMemo(() => new Set(items.filter((it) => it.pending).map((it) => it.question?.uuid)), [items]);
  const totals = useMemo(
    () => ({ count: items.length, points: items.reduce((sum, it) => sum + (Number(it.points) || 0), 0) }),
    [items]
  );
  const saveState = inFlight > 0 ? "saving" : lastWrite === "error" ? "error" : lastWrite === "ok" ? "saved" : "idle";

  return {
    test,
    items,
    loading,
    loadError,
    reload,
    refresh,
    flush,
    inTest,
    pendingIds,
    totals,
    saveState,
    busy: inFlight > 0,
    addQuestions,
    removeItem,
    restoreItem,
    moveItem,
    setPoints,
    movePassage,
    gatherPassage,
    removePassage,
    restorePassage,
    syncPassage,
    updateTest,
    uploadInfoFile,
  };
}
